import { useState } from "react";
import PageHeader from "../components/PageHeader";
import { FaChevronDown } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

export default function Faqs() {
    const faqs = [
        {
            question: "How do I book a hotel through UAEDiscountedHotels?",
            answer:
                "Simply browse our hotel listings, select your preferred property, choose your dates, and complete the booking process. You will receive instant confirmation upon successful payment.",
        },
        {
            question: "Can I cancel or modify my booking?",
            answer:
                "Yes. Cancellation and modification policies vary depending on the hotel, airline, or service provider. Please review the booking terms before confirming your reservation.",
        },
        {
            question: "Do you provide visa assistance?",
            answer:
                "Yes, we offer professional visa assistance services for tourists, business travelers, and transit passengers. Our team will guide you through documentation and application requirements.",
        },
        {
            question: "Do you offer flight booking services?",
            answer:
                "Absolutely. We provide domestic and international flight bookings with access to multiple airlines, competitive fares, and flexible travel options.",
        },
        {
            question: "Are my payments secure?",
            answer:
                "Yes. All transactions are processed through secure encrypted payment gateways to ensure complete protection of your personal and financial information.",
        },
        {
            question: "Do you provide airport transfer services?",
            answer:
                "Yes. We arrange reliable airport transfers including private vehicles, shared transfers, and luxury transportation options.",
        },
        {
            question: "Can I book tours and activities?",
            answer:
                "Yes. We offer city tours, desert safaris, sightseeing experiences, attraction tickets, adventure activities, and customized travel experiences.",
        },
        {
            question: "Do you provide customer support?",
            answer:
                "Our dedicated support team is available to assist you with bookings, travel inquiries, modifications, and general customer service needs.",
        },
    ];

    const [activeIndex, setActiveIndex] = useState(null);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>FAQs | UAE Discounted Hotels - Frequently Asked Questions</title>

                <meta
                    name="description"
                    content="Find answers to frequently asked questions about hotel bookings, hotel deals, payments, cancellations, refunds, check-in, check-out, and reservation support at UAE Discounted Hotels."
                />

                <meta
                    name="keywords"
                    content="UAE hotel FAQs, hotel booking questions, discounted hotels UAE, Dubai hotel booking FAQ, hotel cancellation policy, hotel payment support, UAE Discounted Hotels"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="UAE Discounted Hotels" />

                {/* Canonical */}
                <link
                    rel="canonical"
                    href="https://uaediscountedhotels.com/faqs"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="UAE Discounted Hotels" />
                <meta property="og:locale" content="en_US" />

                <meta
                    property="og:title"
                    content="FAQs | UAE Discounted Hotels"
                />

                <meta
                    property="og:description"
                    content="Get answers to common questions about hotel bookings, discounts, reservations, payments, cancellations, refunds, and customer support."
                />

                <meta
                    property="og:url"
                    content="https://uaediscountedhotels.com/faqs"
                />

                <meta
                    property="og:image"
                    content="https://uaediscountedhotels.com/images/default-og.png"
                />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />

                <meta
                    name="twitter:title"
                    content="FAQs | UAE Discounted Hotels"
                />

                <meta
                    name="twitter:description"
                    content="Find answers to common questions about hotel bookings, payments, cancellations, refunds, and discounted hotel stays across the UAE."
                />

                <meta
                    name="twitter:image"
                    content="https://uaediscountedhotels.com/images/default-og.png"
                />
            </Helmet>

            <div className="w-full bg-gray-50 dark:bg-gray-950">

                {/* PAGE HEADER */}
                <PageHeader
                    title="Frequently Asked Questions"
                    subtitle="Everything you need to know about our travel and booking services"
                />

                {/* FAQ SECTION */}
                <section className="max-w-6xl mx-auto px-4 py-14">

                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                            Frequently Asked Questions
                        </h2>

                        <p className="text-gray-600 dark:text-gray-400 mt-3">
                            Quick answers to the most common questions about
                            UAEDiscountedHotels services.
                        </p>
                    </div>

                    <div className="space-y-5">

                        {faqs.map((item, index) => (
                            <div
                                key={index}
                                className={`
                  bg-white dark:bg-gray-900
                  border border-gray-200 dark:border-gray-800
                  rounded-2xl
                  overflow-hidden

                  shadow-[0_10px_30px_rgba(0,0,0,0.08)]
                  dark:shadow-[0_10px_30px_rgba(0,0,0,0.4)]

                  transition-all duration-300
                  hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]
                  dark:hover:shadow-[0_20px_60px_rgba(0,0,0,0.55)]
                `}
                            >

                                <button
                                    onClick={() => toggleFAQ(index)}
                                    className="w-full flex justify-between items-center px-6 py-5 text-left"
                                >

                                    <span className="font-semibold text-gray-900 dark:text-white text-lg pr-4">
                                        {item.question}
                                    </span>

                                    <div
                                        className="
                      w-10 h-10
                      rounded-xl
                      flex items-center justify-center
                      bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700
                      text-white
                      shrink-0
                    "
                                    >
                                        <FaChevronDown
                                            className={`transition-transform duration-300 ${activeIndex === index ? "rotate-180" : ""
                                                }`}
                                        />
                                    </div>

                                </button>

                                <div
                                    className={`
                    overflow-hidden transition-all duration-300
                    ${activeIndex === index
                                            ? "max-h-60 opacity-100"
                                            : "max-h-0 opacity-0"
                                        }
                  `}
                                >
                                    <div className="px-6 pb-6 border-t border-gray-100 dark:border-gray-800">
                                        <p className="pt-4 text-gray-600 dark:text-gray-400 leading-relaxed">
                                            {item.answer}
                                        </p>
                                    </div>
                                </div>

                            </div>
                        ))}

                    </div>
                </section>

            </div>
        </>
    );
}